import { unionBy } from 'lodash-es';
import React, { useCallback, useMemo, useState } from 'react';

import { useDebounce } from '../../utils/hooks/useDebounce';
import { isEmptyString } from '../../utils/str';
import {
  MultiTypeahead,
  type MultiTypeaheadProps,
  SingleTypeahead,
  type SingleTypeaheadProps,
} from '../inputs/Typeahead';
import { type SelectOptionType } from '../inputs/selects';

import {
  type JobTitleTypeaheadJobTitleFragment,
  useJobTitleTypeaheadQuery,
} from './__generated-gql-types__/JobTitleTypeahead.generated';

export type JobTitleTypeaheadJobTitle = JobTitleTypeaheadJobTitleFragment;

const isJobTitleTypeaheadJobTitleEqual = (
  jt1: JobTitleTypeaheadJobTitle,
  jt2: JobTitleTypeaheadJobTitle,
) => (!jt1.id && !jt2.id ? jt1.name === jt2.name : jt1.id === jt2.id);

type SingleJobTitleTypeaheadProps = Omit<
  SingleTypeaheadProps<JobTitleTypeaheadJobTitle>,
  'options' | 'onInputChange'
>;

export const SingleJobTitleTypeahead: React.FC<
  SingleJobTitleTypeaheadProps
> = ({ value, placeholder = 'Search job titles...', ...props }) => {
  const queryProps = useJobTitleTypeaheadQueryData({ value });

  return (
    <SingleTypeahead
      {...props}
      value={value}
      isValueEqual={isJobTitleTypeaheadJobTitleEqual}
      placeholder={placeholder}
      {...queryProps}
    />
  );
};

type MultiJobTitleTypeaheadProps = Omit<
  MultiTypeaheadProps<JobTitleTypeaheadJobTitle>,
  'options' | 'onInputChange'
>;

export const MultiJobTitleTypeahead: React.FC<MultiJobTitleTypeaheadProps> = ({
  values,
  placeholder = 'Search job titles...',
  ...props
}) => {
  const queryProps = useJobTitleTypeaheadQueryData({ values });

  return (
    <MultiTypeahead
      {...props}
      values={values}
      isValueEqual={isJobTitleTypeaheadJobTitleEqual}
      placeholder={placeholder}
      {...queryProps}
    />
  );
};

const jobTitleToSelectOptionType = (
  jobTitle: JobTitleTypeaheadJobTitle,
): SelectOptionType<JobTitleTypeaheadJobTitle> => ({
  value: jobTitle,
  label: jobTitle.name,
});

const useJobTitleTypeaheadQueryData = ({
  value,
  values,
}: {
  value?: Nullable<JobTitleTypeaheadJobTitle>;
  values?: JobTitleTypeaheadJobTitle[];
}) => {
  const [rawQuery, setRawQuery] = useState<string>('');
  const query = useDebounce(rawQuery, 200);
  const onInputChange = useCallback((input: string) => setRawQuery(input), []);

  const { data, loading } = useJobTitleTypeaheadQuery({
    variables: { query },
    skip: isEmptyString(query),
  });

  const options: Array<SelectOptionType<JobTitleTypeaheadJobTitle>> =
    useMemo(() => {
      const jobTitles = unionBy(
        data?.jobTitleTypeahead ?? [],
        value ? [value] : values,
        'id',
      );
      return jobTitles.map(jobTitleToSelectOptionType);
    }, [data?.jobTitleTypeahead, value, values]);

  return { onInputChange, options, loading };
};
